import React, { useState, useEffect } from "react";
import { message, Button, TimePicker } from "antd";
import dayjs from "dayjs";
import { postRequest, getRequest } from "../service/index";
import { getEmployeeData } from "../utils/localStorage";

const EditShiftModal = ({ onClose, onSuccess, initialData = {} }) => {

  const [loading, setLoading] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const employeeDetails = getEmployeeData();
  // Helper to parse initial time string to dayjs object or null
  const parseTime = (timeStr) => {
    if (!timeStr || timeStr === "-") return null;
    // Accepts "HH:mm" or "HH:mm:ss"
    const parts = timeStr.split(":");
    if (parts.length < 2) return null;
    return dayjs(`${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`, "HH:mm");
  };

  // Use a single formData object for all fields
  const [formData, setFormData] = useState({
    checkIn: parseTime(initialData.checkIn),
    checkOut: parseTime(initialData.checkOut),
    reason: initialData.reason || "",
  });
  const fetchEmployeeData = async () => {
    const response = await getRequest(`/api/companies/teammembers?employeeId=${employeeDetails.userId}`);
    setEmployeeData(response);
    console.log("employeeData",employeeData);
  }
  // Update formData when initialData changes
  useEffect(() => {
    setFormData({
      checkIn: parseTime(initialData.checkIn),
      checkOut: parseTime(initialData.checkOut),
      reason: initialData.reason || "",
    });
    
    fetchEmployeeData();
    // eslint-disable-next-line
  }, [initialData]);

  // Convert dayjs object to "HH:mm" string
  const to24Hour = (timeObj) => {
    if (!timeObj || !dayjs.isDayjs(timeObj)) return "";
    return timeObj.format("HH:mm");
  };

  const handleSubmit = async () => {
    if (!formData.reason) {
      message.error("Please fill in all required fields");
      return;
    }

    // Add status: 'pending' and attendanceLogId to the payload and ensure time is in 24-hour format
    const payload = {
      ...formData,
      changeStartTime: to24Hour(formData.checkIn),
      changeEndTime: to24Hour(formData.checkOut),
      status: "pending",
      requestEditShiftDate:initialData.date,
      attendanceLogId: initialData._id || null,
    };

    try {
      setLoading(true);
      const response = await postRequest("/api/requestshift", payload);
       const managementIds = (employeeData?.managementChain || []).map(m => m?._id).filter(Boolean);
       const uniqueReceiverIds = Array.from(new Set([employeeDetails?.companyId, ...managementIds].filter(Boolean)));
       if (response.success || response.message === "RequestShift created successfully") {
        const res = await postRequest('/api/notifications', {
          "title": `${employeeData.employee.name} Request Edit Shift`,
          "senderId": employeeDetails.userId,
          "receiverIds": uniqueReceiverIds,
          "type": "RequestShift",
          "relatedTypeId": response.requestShift._id,
          "relatedDocumentModel": "RequestShift",
          "companyId": employeeDetails?.companyId,
          "priority": "medium"
        });
        message.success(response.message || "RequestShift created successfully");
        onSuccess && onSuccess();
        onClose();
      } else {
        message.error(response.error || "Failed to update shift");
      }
    } catch (error) {
      console.error("Error updating shift:", error);
      message.error("Failed to update shift. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 max-h-[80vh] overflow-y-auto">
      <div className="flex flex-col gap-1">
        <label htmlFor="check-in" className="text-md">
          Check In
        </label>
        <TimePicker
          id="check-in"
          className="w-full"
          format="HH:mm"
          value={formData.checkIn}
          onChange={(value) => setFormData({ ...formData, checkIn: value })}
          allowClear
          minuteStep={1}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="check-out" className="text-md">
          Check Out
        </label>
        <TimePicker
          id="check-out"
          className="w-full"
          format="HH:mm"
          value={formData.checkOut}
          onChange={(value) => setFormData({ ...formData, checkOut: value })}
          allowClear
          minuteStep={1}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="reason" className="text-md">
          Reason
        </label>
        <textarea
          id="reason"
          className="border border-gray-400 outline-none px-2 py-1 rounded-md text-md resize-none"
          placeholder="Enter reason for shift edit"
          value={formData.reason}
          onChange={e => setFormData({ ...formData, reason: e.target.value })}
          rows={3}
        />
      </div>

      <div className="flex items-center justify-end gap-3 my-2">
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          loading={loading}
          type="primary"
          className="bg-primary text-white"
        >
          {loading ? "Adding..." : "Request Shift"}
        </Button>
      </div>
    </div>
  );
};

export default EditShiftModal;
